import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import HelpIcon from '@mui/icons-material/Help';
import { Input } from '@components/Input';
import { MultiSelect } from '@components/MultiSelect';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import Autocomplete from '@components/Autocomplete';
import FieldWithActions, { rowDeleteAction } from '@components/FieldWithActions';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { alertActions } from '@actions';
import { teamsService } from '@services/teams';
import { handleApiError } from '@helpers/api';
import { getFullName } from '@helpers/userName';
import { Modal, Content, PermissionRow, TabPanel, MembersList, MemberRow } from './styles';
import { StyledRadioGroup } from '../../UserManagement/EditModal/styles';

export const FORM_TYPE = {
    NEW: 'NEW',
    EDIT: 'EDIT',
};

const SCOPE_TYPE = {
    GLOBAL: 0,
    BRAND: 1,
    LOCATION: 2,
};

const TEAM_TAB = {
    MEMBERS: 0,
    PERMISSIONS: 1,
};

const EMPTY_PICKER_VALUE = [];

const PERMISSION_LEVELS = {
    NONE: 'none',
    RESTRICTED: 'restricted',
    FULL: 'full',
};

const LEVEL_RANK = {
    [PERMISSION_LEVELS.NONE]: 0,
    [PERMISSION_LEVELS.RESTRICTED]: 1,
    [PERMISSION_LEVELS.FULL]: 2,
};

const getDefaultTier = mod =>
    mod.tiers?.includes(PERMISSION_LEVELS.NONE) ? PERMISSION_LEVELS.NONE : PERMISSION_LEVELS.RESTRICTED;

const TeamEditModal = ({ selectedTeam, setSelectedTeam, modal, setModal, brands, account, onSaved }) => {
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [catalog, setCatalog] = useState(null);
    const [activeTab, setActiveTab] = useState(TEAM_TAB.MEMBERS);
    const [memberSearch, setMemberSearch] = useState('');

    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const dispatch = useDispatch();
    const { mapSelectedLocationsToFormValue, getLocationsOptions, hasSingleLocation } = useLocations();

    const TeamSchema = yup.object().shape(
        {
            name: yup.string().required('validation.required').max(80, 'validation.maxLength'),
            scopeType: yup.string(),
            brandIds: yup.array().when('scopeType', {
                is: `${SCOPE_TYPE.BRAND}`,
                then: schema => schema.min(1, 'validation.required'),
            }),
            locationIds: yup.array().when('scopeType', {
                is: `${SCOPE_TYPE.LOCATION}`,
                then: schema => schema.min(1, 'validation.required'),
            }),
        },
        [['scopeType', 'brandIds']],
    );

    const resolver = useYupValidationResolver(TeamSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            name: '',
            permissions: {},
            settingsPermission: PERMISSION_LEVELS.NONE,
            scopeType: `${SCOPE_TYPE.GLOBAL}`,
            brandIds: [],
            locationIds: [],
            memberIds: [],
        },
    });

    const scopeType = watch('scopeType');
    const permissionsValue = watch('permissions');
    const memberIds = watch('memberIds');

    const brandsOptions = brands.map(brand => ({ value: brand.id, label: brand.title }));
    const locationsOptions = useMemo(() => getLocationsOptions(), [getLocationsOptions]);

    const usersOptions = useMemo(() => {
        return (account?.users || []).map(user => ({
            value: user.id,
            label: getFullName(user.firstName, user.lastName) || user.email,
        }));
    }, [account?.users]);

    const selectedMembers = useMemo(
        () => (memberIds || []).map(id => usersOptions.find(u => u.value === id)).filter(Boolean),
        [memberIds, usersOptions],
    );

    const availableMemberOptions = useMemo(() => {
        const notSelected = usersOptions.filter(u => !(memberIds || []).includes(u.value));
        const search = memberSearch.trim().toLowerCase();
        const filtered = search ? notSelected.filter(u => u.label.toLowerCase().includes(search)) : notSelected;
        return filtered.map(u => ({ id: `${u.value}`, label: u.label }));
    }, [usersOptions, memberIds, memberSearch]);

    const addMember = userId =>
        setValue('memberIds', [...(memberIds || []), userId], { shouldDirty: true, shouldValidate: true });

    const removeMember = userId =>
        setValue(
            'memberIds',
            (memberIds || []).filter(id => id !== userId),
            {
                shouldDirty: true,
                shouldValidate: true,
            },
        );

    // Load permissions catalog from server — single source of truth for module keys and labels
    useEffect(() => {
        teamsService
            .getPermissionsCatalog()
            .then(data => setCatalog(data))
            .catch(() => setCatalog(null));
    }, []);

    const MODULE_LABELS = {
        contacts: t('Sidebar.contacts'),
        locations: t('Sidebar.locations'),
        reviews: t('Sidebar.reviews'),
        posts: t('Sidebar.social'),
        messaging: t('Sidebar.messaging'),
        microsites: t('Sidebar.webPages'),
        surveys: t('Sidebar.surveys'),
        campaigns: t('Sidebar.campaigns'),
        coupons: t('Sidebar.coupons'),
    };

    const getModuleTooltip = mod => (
        <div>
            {mod.tiers?.includes(PERMISSION_LEVELS.NONE) && (
                <div style={{ marginBottom: 4 }}>
                    <strong>{t('SettingsTeams.none')}:</strong> {t('SettingsTeams.noneTooltip')}
                </div>
            )}
            {mod.tiers?.includes(PERMISSION_LEVELS.RESTRICTED) && (
                <div style={{ marginBottom: 4 }}>
                    <strong>{t('SettingsTeams.tooltipRestricted')}:</strong> {t(`SettingsTeams.${mod.key}Restricted`)}
                </div>
            )}
            <div>
                <strong>{t('SettingsTeams.tooltipFull')}:</strong> {t(`SettingsTeams.${mod.key}Full`)}
            </div>
        </div>
    );

    const getSettingsTooltip = () => (
        <div>
            <div style={{ marginBottom: 4 }}>
                <strong>{t('SettingsTeams.none')}:</strong> {t('SettingsTeams.settingsNoneTooltip')}
            </div>
            <div style={{ marginBottom: 4 }}>
                <strong>{t('SettingsTeams.tooltipRestricted')}:</strong> {t('SettingsTeams.settingsRestrictedTooltip')}
            </div>
            <div>
                <strong>{t('SettingsTeams.tooltipFull')}:</strong> {t('SettingsTeams.settingsFullTooltip')}
            </div>
        </div>
    );

    const navModules = catalog?.modules || [];

    const parsePermissionsFromArray = permissionsArray => {
        const permissionsMap = {};
        let settingsLevel = PERMISSION_LEVELS.NONE;

        (permissionsArray || []).forEach(p => {
            const [module, level] = p.split(':');
            if (!LEVEL_RANK[level]) return;
            if (module === 'settings') {
                if (LEVEL_RANK[level] > LEVEL_RANK[settingsLevel]) {
                    settingsLevel = level;
                }
            } else if (LEVEL_RANK[level] > LEVEL_RANK[permissionsMap[module] || PERMISSION_LEVELS.NONE]) {
                permissionsMap[module] = level;
            }
        });

        return { permissionsMap, settingsLevel };
    };

    const buildPermissionsArray = (permissionsMap, settingsLevel) => {
        const result = [];
        navModules.forEach(mod => {
            const level = permissionsMap[mod.key] || getDefaultTier(mod);
            if (level !== PERMISSION_LEVELS.NONE) {
                result.push(`${mod.key}:${level}`);
            }
        });
        if (settingsLevel && settingsLevel !== PERMISSION_LEVELS.NONE) {
            result.push(`settings:${settingsLevel}`);
        }
        return result;
    };

    useEffect(() => {
        if (modal.type === FORM_TYPE.EDIT && selectedTeam) {
            setIsLoadingDetail(true);
            teamsService
                .getTeam(selectedTeam.id)
                .then(team => {
                    setValue('name', team.name || '', { shouldValidate: true });

                    const { permissionsMap, settingsLevel } = parsePermissionsFromArray(team.permissions);
                    setValue('permissions', permissionsMap);
                    setValue('settingsPermission', settingsLevel);

                    setValue('scopeType', `${team.scopeType ?? SCOPE_TYPE.GLOBAL}`, { shouldValidate: true });
                    setValue(
                        'brandIds',
                        (team.brands || []).map(b => b.id),
                        { shouldValidate: true },
                    );
                    setValue(
                        'locationIds',
                        mapSelectedLocationsToFormValue((team.locations || []).map(l => l.id)) || [],
                        {
                            shouldValidate: true,
                        },
                    );
                    setValue(
                        'memberIds',
                        (team.members || []).map(m => m.userId),
                        { shouldValidate: true },
                    );
                })
                .catch(error => {
                    handleApiError(error, 'alertMessages.loadFail');
                })
                .finally(() => {
                    setIsLoadingDetail(false);
                });
        }
    }, [selectedTeam, modal.type, setValue]);

    const onClose = () => {
        setModal({ open: false, type: FORM_TYPE.NEW });
        setSelectedTeam(null);
    };

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: { success: t('alertMessages.editSuccess'), error: t('alertMessages.editFail') },
        [FORM_TYPE.NEW]: { success: t('alertMessages.createSuccess'), error: t('alertMessages.createFail') },
    };

    const onSubmit = async () => {
        const values = getValues();
        const permissions = buildPermissionsArray(values.permissions, values.settingsPermission);
        const payload = {
            name: values.name,
            permissions,
            scopeType: Number(values.scopeType),
            brandIds: values.scopeType === `${SCOPE_TYPE.BRAND}` ? values.brandIds : [],
            locationIds:
                values.scopeType === `${SCOPE_TYPE.LOCATION}` ? values.locationIds.map(loc => loc.id || loc) : [],
            memberUserIds: values.memberIds,
        };

        let savedTeam;
        try {
            savedTeam =
                modal.type === FORM_TYPE.EDIT
                    ? await teamsService.updateTeam(selectedTeam.id, payload)
                    : await teamsService.createTeam(payload);
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error);
            return;
        }

        dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
        await onSaved(savedTeam);
        onClose();
    };

    const catalogReady = navModules.length > 0;
    const hasScopedSelection =
        catalogReady &&
        navModules.some(
            mod => !mod.isGlobal && (permissionsValue?.[mod.key] || getDefaultTier(mod)) !== PERMISSION_LEVELS.NONE,
        );
    const scopeHidden = hasSingleLocation || !hasScopedSelection;

    useEffect(() => {
        if (!hasSingleLocation && (!catalogReady || isLoadingDetail)) return;
        if (scopeHidden) {
            setValue('scopeType', `${SCOPE_TYPE.GLOBAL}`, { shouldDirty: true, shouldValidate: true });
        }
    }, [scopeHidden, catalogReady, isLoadingDetail, hasSingleLocation, setValue]);

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onClose}
            isDirty={isDirty}
            title={modal.type === FORM_TYPE.NEW ? t('SettingsTeams.createTeam') : t('SettingsTeams.editTeam')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty || isLoadingDetail || !catalogReady}
        >
            <Content>
                <div style={{ gridColumn: !isMobile && '1 / span 2' }}>
                    <Controller
                        control={control}
                        name="name"
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                inputProps={{ ...field, maxLength: 80 }}
                                label={t('SettingsTeams.teamName')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                </div>

                <TabPanel>
                    <RadioButtonsGroup
                        name="team-tab"
                        value={activeTab}
                        onChange={setActiveTab}
                        options={[
                            { label: t('SettingsTeams.members'), value: TEAM_TAB.MEMBERS },
                            { label: t('SettingsTeams.permissions'), value: TEAM_TAB.PERMISSIONS },
                        ]}
                    />
                </TabPanel>

                {activeTab === TEAM_TAB.PERMISSIONS && (
                    <div style={{ gridColumn: !isMobile && '1 / span 2' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'var(--text-primary)',
                                marginBottom: '4px',
                            }}
                        >
                            {t('SettingsTeams.permissions')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                display: 'inline-block',
                                marginBottom: '16px',
                            }}
                        >
                            {t('SettingsTeams.permissionsDescription')}
                        </Typography>

                        {navModules.map(mod => (
                            <PermissionRow key={mod.key}>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {MODULE_LABELS[mod.key] || mod.label}
                                    {mod.isGlobal && (
                                        <Chip
                                            label={t('SettingsTeams.globalPermission')}
                                            size="small"
                                            sx={{ height: '18px', fontSize: '11px' }}
                                        />
                                    )}
                                    <IconButton sx={{ padding: '2px' }}>
                                        <Tooltip title={getModuleTooltip(mod)} placement="top" enterTouchDelay={0}>
                                            <HelpIcon sx={{ fontSize: '16px' }} />
                                        </Tooltip>
                                    </IconButton>
                                </Typography>
                                <Controller
                                    control={control}
                                    name={`permissions.${mod.key}`}
                                    render={({ field }) => (
                                        <RadioButtonsGroup
                                            name={`perm-${mod.key}`}
                                            value={field.value || getDefaultTier(mod)}
                                            onChange={value => {
                                                setValue(`permissions.${mod.key}`, value, {
                                                    shouldDirty: true,
                                                });
                                            }}
                                            options={(
                                                mod.tiers || [PERMISSION_LEVELS.RESTRICTED, PERMISSION_LEVELS.FULL]
                                            ).map(tier => ({
                                                value: tier,
                                                label: t(`SettingsTeams.${tier}`),
                                            }))}
                                        />
                                    )}
                                />
                            </PermissionRow>
                        ))}

                        <PermissionRow>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {t('SettingsTeams.accountSettings')}
                                <Chip
                                    label={t('SettingsTeams.globalPermission')}
                                    size="small"
                                    sx={{ height: '18px', fontSize: '11px' }}
                                />
                                <IconButton sx={{ padding: '2px' }}>
                                    <Tooltip title={getSettingsTooltip()} placement="top" enterTouchDelay={0}>
                                        <HelpIcon sx={{ fontSize: '16px' }} />
                                    </Tooltip>
                                </IconButton>
                            </Typography>
                            <Controller
                                control={control}
                                name="settingsPermission"
                                render={({ field }) => (
                                    <RadioButtonsGroup
                                        name="perm-settings"
                                        value={field.value || PERMISSION_LEVELS.NONE}
                                        onChange={value => {
                                            setValue('settingsPermission', value, {
                                                shouldDirty: true,
                                            });
                                        }}
                                        options={[
                                            { value: PERMISSION_LEVELS.NONE, label: t('SettingsTeams.none') },
                                            {
                                                value: PERMISSION_LEVELS.RESTRICTED,
                                                label: t('SettingsTeams.restricted'),
                                            },
                                            { value: PERMISSION_LEVELS.FULL, label: t('SettingsTeams.full') },
                                        ]}
                                    />
                                )}
                            />
                        </PermissionRow>
                    </div>
                )}

                {activeTab === TEAM_TAB.PERMISSIONS && !scopeHidden && (
                    <div style={{ gridColumn: !isMobile && '1 / span 2' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'var(--text-primary)',
                                marginBottom: '4px',
                            }}
                        >
                            {t('SettingsTeams.scope')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                display: 'inline-block',
                                marginBottom: '8px',
                            }}
                        >
                            {t('SettingsTeams.scopeDescription')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                display: 'inline-block',
                                marginBottom: '16px',
                            }}
                        >
                            {t('SettingsTeams.scopeScopedOnlyNote')}
                        </Typography>
                        <Controller
                            control={control}
                            name="scopeType"
                            render={({ field }) => (
                                <div>
                                    {[
                                        { id: `${SCOPE_TYPE.GLOBAL}`, name: t('SettingsUsers.globalAccess') },
                                        { id: `${SCOPE_TYPE.BRAND}`, name: t('SettingsUsers.brandAccess') },
                                        { id: `${SCOPE_TYPE.LOCATION}`, name: t('SettingsUsers.locationAccess') },
                                    ].map(level => (
                                        <Fragment key={level.id}>
                                            <StyledRadioGroup
                                                value={field.value}
                                                onChange={e =>
                                                    setValue('scopeType', e.target.value, {
                                                        shouldDirty: true,
                                                        shouldValidate: true,
                                                    })
                                                }
                                            >
                                                <FormControlLabel
                                                    value={`${level.id}`}
                                                    control={
                                                        <Radio
                                                            sx={{
                                                                color: 'var(--text-secondary)',
                                                                padding: '6px',
                                                                '&.Mui-checked': { color: 'var(--primary-main)' },
                                                            }}
                                                            disabled={isSubmitting}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                                            {level.name}
                                                        </Typography>
                                                    }
                                                    disabled={isSubmitting}
                                                />
                                            </StyledRadioGroup>
                                            {scopeType === `${SCOPE_TYPE.BRAND}` &&
                                                level.id === `${SCOPE_TYPE.BRAND}` && (
                                                    <Controller
                                                        control={control}
                                                        name="brandIds"
                                                        render={({ field, fieldState: { error }, ref }) => (
                                                            <MultiSelect
                                                                {...field}
                                                                ref={ref}
                                                                label={t('common.brands')}
                                                                onChange={value => {
                                                                    setValue('brandIds', value, {
                                                                        shouldDirty: true,
                                                                        shouldValidate: true,
                                                                    });
                                                                }}
                                                                options={brandsOptions}
                                                                countable
                                                                required
                                                                error={error}
                                                                style={{ marginTop: '8px' }}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            {scopeType === `${SCOPE_TYPE.LOCATION}` &&
                                                level.id === `${SCOPE_TYPE.LOCATION}` && (
                                                    <Controller
                                                        control={control}
                                                        name="locationIds"
                                                        render={({ field, fieldState: { error }, ref }) => (
                                                            <Autocomplete
                                                                {...field}
                                                                ref={ref}
                                                                label={t('common.locations')}
                                                                options={locationsOptions}
                                                                value={field.value}
                                                                isOptionEqualToValue={(option, value) =>
                                                                    option.id === value.id
                                                                }
                                                                inputProps={{
                                                                    style: {
                                                                        display:
                                                                            field?.value?.length > 0 ? 'none' : 'block',
                                                                    },
                                                                }}
                                                                onChangeAutoComplete={items => {
                                                                    setValue('locationIds', items.length ? items : [], {
                                                                        shouldDirty: true,
                                                                        shouldValidate: true,
                                                                    });
                                                                }}
                                                                groupMultiProps={{
                                                                    getOptionLabel: option => option.label,
                                                                }}
                                                                required
                                                                error={error}
                                                                className="companies-autocomplete"
                                                                withCaret
                                                            />
                                                        )}
                                                    />
                                                )}
                                        </Fragment>
                                    ))}
                                </div>
                            )}
                        />
                    </div>
                )}

                {activeTab === TEAM_TAB.MEMBERS && (
                    <div style={{ gridColumn: !isMobile && '1 / span 2' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'var(--text-primary)',
                                marginBottom: '4px',
                            }}
                        >
                            {t('SettingsTeams.members')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                display: 'inline-block',
                                marginBottom: '16px',
                            }}
                        >
                            {t('SettingsTeams.membersDescription')}
                        </Typography>
                        <Autocomplete
                            label={t('SettingsTeams.addMember')}
                            options={availableMemberOptions}
                            value={EMPTY_PICKER_VALUE}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            inputProps={{
                                value: memberSearch,
                                onChange: e => setMemberSearch(e.target.value),
                                style: { display: 'block' },
                            }}
                            onChangeAutoComplete={items => {
                                const picked = items[items.length - 1];
                                if (picked) {
                                    addMember(Number(picked.id));
                                    setMemberSearch('');
                                }
                            }}
                            keepOpenOnSelect
                            withCaret
                        />
                        {selectedMembers.length > 0 && (
                            <MembersList>
                                {selectedMembers.map(member => (
                                    <FieldWithActions
                                        key={member.value}
                                        actions={rowDeleteAction({ onDelete: () => removeMember(member.value), t })}
                                    >
                                        <MemberRow>
                                            <Typography variant="body2">{member.label}</Typography>
                                        </MemberRow>
                                    </FieldWithActions>
                                ))}
                            </MembersList>
                        )}
                    </div>
                )}
            </Content>
        </Modal>
    );
};

export default TeamEditModal;
